import { useState } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { MONTHS } from './GridConfig'

const SCOPE_OPTIONS = [
  { value: 'AllProduct', label: 'All Product' },
  { value: 'BriteCode', label: 'Brite Code' },
  { value: 'ItemNumber', label: 'Item Number' },
]

const CHANGE_TYPE_OPTIONS = [
  { value: 'PercentOfMinInv', label: '% of Min Inv' },
  { value: 'Qty', label: 'Qty' },
]

export default function NewModificationForm({ planId, onCreated }) {
  const [scope, setScope] = useState('AllProduct')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [changeType, setChangeType] = useState('PercentOfMinInv')
  const [value, setValue] = useState('')
  const [monthMode, setMonthMode] = useState('all')
  const [selectedMonths, setSelectedMonths] = useState(
    MONTHS.reduce((acc, m) => ({ ...acc, [m.key]: true }), {}),
  )
  const [submitting, setSubmitting] = useState(false)

  const toggleMonth = (key) => {
    setSelectedMonths((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSubmit = async () => {
    if (!name.trim() || !value) return

    const numericValue = parseFloat(value)
    if (isNaN(numericValue)) return

    setSubmitting(true)
    try {
      const monthValues = {}
      const activeMonths = monthMode === 'all'
        ? MONTHS
        : MONTHS.filter((m) => selectedMonths[m.key])

      activeMonths.forEach((m) => {
        monthValues[m.key] = numericValue
      })

      const payload = {
        planId,
        name: name.trim(),
        scope,
        identifier: scope !== 'AllProduct' ? identifier.trim() : '',
        changeType,
        defaultValue: numericValue,
        monthValues,
      }

      const { api } = await import('../../services/api')
      const result = await api.post('/modifications', payload)
      onCreated?.(result)

      setName('')
      setValue('')
      setIdentifier('')
    } catch (err) {
      console.error('Failed to create modification:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const needsIdentifier = scope === 'BriteCode' || scope === 'ItemNumber'
  const identifierLabel = scope === 'BriteCode' ? 'Brite Code' : 'Item Number'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        New Modification
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Scope</InputLabel>
          <Select value={scope} onChange={(e) => setScope(e.target.value)} label="Scope">
            {SCOPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsIdentifier && (
          <TextField
            size="small"
            label={identifierLabel}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            sx={{ width: 160 }}
          />
        )}

        <TextField
          size="small"
          label="Description"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: 1, minWidth: 180 }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Change Type</InputLabel>
          <Select value={changeType} onChange={(e) => setChangeType(e.target.value)} label="Change Type">
            {CHANGE_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={changeType === 'PercentOfMinInv' ? 'Multiplier (e.g. 1.30)' : 'Cases'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type="number"
          sx={{ width: 160 }}
        />

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip
            label="All 12"
            size="small"
            variant={monthMode === 'all' ? 'filled' : 'outlined'}
            color={monthMode === 'all' ? 'primary' : 'default'}
            onClick={() => setMonthMode('all')}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          <Chip
            label="Custom"
            size="small"
            variant={monthMode === 'custom' ? 'filled' : 'outlined'}
            color={monthMode === 'custom' ? 'primary' : 'default'}
            onClick={() => setMonthMode('custom')}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<Add sx={{ fontSize: 16 }} />}
          onClick={handleSubmit}
          disabled={submitting || !name.trim() || !value}
          sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
        >
          Apply
        </Button>
      </Box>

      {monthMode === 'custom' && (
        <FormGroup row sx={{ gap: 0 }}>
          {MONTHS.map((m) => (
            <FormControlLabel
              key={m.key}
              control={
                <Checkbox
                  checked={selectedMonths[m.key] || false}
                  onChange={() => toggleMonth(m.key)}
                  size="small"
                />
              }
              label={m.label.split(' ')[0]}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
            />
          ))}
        </FormGroup>
      )}
    </Box>
  )
}
